import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin } from 'lucide-react';
import { Container } from './Container';
import { Button } from './Button';
import { scrollToElement } from '../utils';
import { useNavigate } from 'react-router-dom';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface FooterProps {
  logo?: string;
  description?: string;
  sections?: FooterSection[];
  contactInfo?: {
    phone?: string;
    email?: string;
    address?: string;
  };

  className?: string;
}

/**
 * 页脚组件
 * 包含公司信息、导航链接、联系方式和社交媒体链接
 */
export const Footer: React.FC<FooterProps> = ({
  logo = '车为家',
  description = '专业的汽车智能化解决方案提供商，致力于为客户提供最优质的产品和服务。',
  sections = [],
  contactInfo = {},

  className = '',
}) => {
  const currentYear = new Date().getFullYear();
  
  const navigate = useNavigate();
  
  // 处理链接点击
  const handleLinkClick = (href: string) => {
    if (href.startsWith('#')) {
      const elementId = href.replace('#', '');
      scrollToElement(elementId);
    } else if (href.startsWith('/')) {
      // 内部路由跳转
      if (href.includes('#')) {
        const [path, hash] = href.split('#');
        navigate(path);
        // 延迟滚动到锚点，确保页面已加载
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        navigate(href);
      }
    } else {
      // 外部链接
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };
  
  // 动画配置
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut' as const,
      },
    },
  };
  
  return (
    <footer className={`bg-gray-900 text-white ${className}`}>
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="py-16"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* 公司信息 */}
            <motion.div variants={itemVariants} className="lg:col-span-2">
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-4">{logo}</h3>
                <p className="text-gray-300 leading-relaxed max-w-md">
                  {description}
                </p>
              </div>
              
              {/* 联系信息 */}
              <div className="space-y-3">
                {contactInfo.phone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-primary-400" />
                    <span className="text-gray-300">{contactInfo.phone}</span>
                  </div>
                )}
                
                {contactInfo.email && (
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-primary-400" />
                    <span className="text-gray-300">{contactInfo.email}</span>
                  </div>
                )}
                
                {contactInfo.address && (
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-primary-400 mt-0.5" />
                    <span className="text-gray-300">{contactInfo.address}</span>
                  </div>
                )}
              </div>
              

            </motion.div>
            
            {/* 导航链接 */}
            {sections.map((section, index) => (
              <motion.div key={section.title} variants={itemVariants}>
                <h4 className="text-lg font-semibold mb-4">{section.title}</h4>
                <ul className="space-y-2">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <button
                        onClick={() => handleLinkClick(link.href)}
                        className="text-gray-300 hover:text-primary-400 transition-colors duration-300"
                      >
                        {link.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
            
            {/* 关注我们模块 */}
            <motion.div variants={itemVariants}>
              <h4 className="text-lg font-semibold mb-4">关注我们</h4>
              <p className="text-gray-300 text-sm mb-4">关注公众号，了解更多资讯</p>
              <div className="flex justify-start">
                <img 
                  src="/qr-code-miniapp.jpg" 
                  alt="关注公众号" 
                  className="w-24 h-24 object-contain bg-white rounded-lg p-2"
                />
              </div>
            </motion.div>
          </div>
          
          {/* 分隔线 */}
          <motion.div
            variants={itemVariants}
            className="border-t border-gray-800 mt-12 pt-8"
          >
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-gray-400 text-sm">
                &copy; {currentYear} {logo}. 保留所有权利。
              </p>
              
              <div className="flex space-x-6 text-sm">
                <button
                  onClick={() => handleLinkClick('#privacy')}
                  className="text-gray-400 hover:text-primary-400 transition-colors duration-300"
                >
                  隐私政策
                </button>
                <button
                  onClick={() => handleLinkClick('#terms')}
                  className="text-gray-400 hover:text-primary-400 transition-colors duration-300"
                >
                  服务条款
                </button>
                <button
                  onClick={() => handleLinkClick('#sitemap')}
                  className="text-gray-400 hover:text-primary-400 transition-colors duration-300"
                >
                  网站地图
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </Container>
    </footer>
  );
};